/**
 * Page publique de préqualification MaPrimeAdapt' — magic link mobile-first.
 * Route: /preq/:token  (hors ProtectedRoute, pas d'auth).
 * Autosave toutes les 3s. Soumission = verrou définitif.
 */
import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CheckCircle2, AlertTriangle, XCircle, Loader2, Info,
  ShieldCheck, HelpCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BENEF_OPTIONS = [
  { v: '70_plus', label: '70 ans ou plus' },
  { v: '60_69_gir', label: '60 à 69 ans avec un GIR reconnu' },
  { v: 'handicap', label: 'Situation de handicap avec taux d\'incapacité ≥ 50 % OU éligibilité à la PCH' },
  { v: 'autre', label: 'Autre situation' },
  { v: 'inconnu', label: 'Je ne sais pas' },
];
const OCCUPATION_OPTIONS = [
  { v: 'proprio_occupant', label: 'Propriétaire occupant' },
  { v: 'proprio_bailleur', label: 'Propriétaire bailleur' },
  { v: 'locataire_prive', label: 'Locataire du parc privé' },
  { v: 'locataire_social', label: 'Locataire du parc social' },
  { v: 'inconnu', label: 'Je ne sais pas' },
];
const BAILLEUR_OPTIONS = [
  { v: 'obtenu', label: 'Obtenu (accord écrit)' },
  { v: 'a_demander', label: 'À demander' },
  { v: 'refuse', label: 'Refusé' },
  { v: 'inconnu', label: 'Je ne sais pas' },
];
const RESIDENCE_OPTIONS = [
  { v: 'oui', label: 'Oui' },
  { v: 'non', label: 'Non' },
  { v: 'inconnu', label: 'Je ne sais pas' },
];

export default function PreqQuizPage() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const draftTimer = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/prospect-quiz/${token}`);
      setState({ loading: false, error: null, data: r.data });
      if (r.data.submitted) {
        setSubmitted(r.data.evaluation);
        setAnswers(r.data.draft || {});
      } else {
        // Fusionner prefill (lecture seule par défaut) et draft (saisie en cours)
        setAnswers({ ...r.data.prefill, ...r.data.draft });
      }
    } catch (e) {
      setState({
        loading: false,
        error: e?.response?.data?.detail || 'Lien invalide ou expiré.',
        data: null,
      });
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Autosave toutes les 3s si des changements
  const saveDraft = useCallback(async (data) => {
    if (!token || submitted) return;
    try {
      await axios.post(`${API}/prospect-quiz/${token}/save-draft`, data);
    } catch (e) {
      // silent — on retente au prochain tick
    }
  }, [token, submitted]);

  useEffect(() => {
    if (draftTimer.current) clearTimeout(draftTimer.current);
    draftTimer.current = setTimeout(() => saveDraft(answers), 3000);
    return () => draftTimer.current && clearTimeout(draftTimer.current);
  }, [answers, saveDraft]);

  const setA = (patch) => setAnswers((p) => ({ ...p, ...patch }));

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/prospect-quiz/${token}/submit`, answers);
      setSubmitted(r.data.evaluation);
      toast.success('Questionnaire envoyé — merci !');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur à l\'envoi');
    } finally { setSubmitting(false); }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <Loader2 className="w-6 h-6 animate-spin text-[#1C49A6]" />
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md bg-white rounded-xl shadow p-6 text-center">
          <XCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <h1 className="text-lg font-bold text-slate-900 mb-2">Lien non valide</h1>
          <p className="text-sm text-slate-600">{state.error}</p>
          <p className="text-xs text-slate-400 mt-3">
            Besoin d'aide ? Répondez à notre email ou contactez Reno Resolve.
          </p>
        </div>
      </div>
    );
  }

  const d = state.data;
  const isDemo = d.is_demo;

  // Vue "soumis"
  if (submitted) {
    const color = submitted.verdict_color;
    const Icon = color === 'emerald' ? CheckCircle2
                : color === 'amber' ? AlertTriangle
                : color === 'red' ? XCircle : Info;
    const bg = { emerald: 'bg-emerald-50 border-emerald-200',
                  amber: 'bg-amber-50 border-amber-200',
                  red: 'bg-red-50 border-red-200',
                  slate: 'bg-slate-50 border-slate-200' }[color] || 'bg-slate-50';
    const iconColor = { emerald: 'text-emerald-600', amber: 'text-amber-600',
                         red: 'text-red-600', slate: 'text-slate-500' }[color];
    return (
      <div className="min-h-screen bg-slate-50 p-4">
        <div className="max-w-xl mx-auto bg-white rounded-xl shadow p-5 sm:p-8 space-y-5"
             data-testid="quiz-submitted-view">
          <div className="text-center">
            <Icon className={`w-14 h-14 mx-auto ${iconColor}`} />
            <h1 className="text-xl font-bold text-slate-900 mt-3">
              Merci {d.prospect_name?.split(' ')[0] || ''} !
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Votre questionnaire a été transmis.
            </p>
          </div>
          <div className={`border rounded-lg p-4 ${bg}`}>
            <div className="text-[11px] uppercase font-semibold text-slate-500">
              Résultat provisoire (indicatif — un conseiller vous recontactera)
            </div>
            <div className="text-base font-bold text-slate-900 mt-1">
              {submitted.verdict_label}
            </div>
          </div>
          {submitted.reasons_block?.length > 0 && (
            <div className="space-y-1.5">
              <div className="text-xs font-semibold text-slate-700">Points bloquants :</div>
              {submitted.reasons_block.map((r, i) => (
                <div key={i} className="text-xs text-red-700 bg-red-50 border border-red-100 rounded p-2">
                  {r.message}
                </div>
              ))}
            </div>
          )}
          {submitted.reasons_verify?.length > 0 && (
            <div className="space-y-1.5">
              <div className="text-xs font-semibold text-slate-700">À vérifier :</div>
              {submitted.reasons_verify.map((r, i) => (
                <div key={i} className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded p-2">
                  {r.message}
                </div>
              ))}
            </div>
          )}
          <p className="text-[11px] text-slate-400 text-center">
            Un conseiller Reno Resolve vous contactera prochainement.
          </p>
        </div>
      </div>
    );
  }

  const isLocataire = answers.q2_occupation === 'locataire_prive' || answers.q2_occupation === 'locataire_social';

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <div className="max-w-xl mx-auto bg-white sm:rounded-xl sm:shadow sm:mt-6 min-h-[80vh]"
           data-testid="quiz-form">
        {/* Header avec logo officiel */}
        <div className="p-4 sm:p-6 border-b border-slate-100">
          <div className="flex justify-center mb-4">
            <img src="/logo.png" alt="Reno Resolve" className="h-10 sm:h-12 w-auto" />
          </div>
          <div className="flex items-center gap-2 text-[11px] text-emerald-700 font-medium">
            <ShieldCheck className="w-3.5 h-3.5" />
            Lien personnel sécurisé — valide 7 jours
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 mt-2">
            Bonjour {d.prospect_name?.split(' ')[0] || ''} 👋
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Merci pour votre demande MaPrimeAdapt'. Pour qualifier votre projet rapidement,
            merci de répondre à ces 6 questions (environ 2 minutes).
          </p>
          {/* V3.1l-fix8 — Bannière "Dossier de démonstration" retirée.
              La nature démo reste tracée côté serveur (is_demo + timeline),
              invisible du prospect pour validation du rendu réel. */}
        </div>

        {/* Récapitulatif projet */}
        <div className="px-4 sm:px-6 pt-5">
          <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg text-sm">
            <div className="text-[11px] uppercase font-semibold text-blue-700 mb-0.5">Votre projet</div>
            <div className="text-slate-900 font-medium">
              {d.project_label}
            </div>
            <label className="flex items-start gap-2 mt-2 cursor-pointer text-xs text-slate-700">
              <input type="checkbox"
                     checked={!!answers.projet_confirme}
                     onChange={(e) => setA({ projet_confirme: e.target.checked })}
                     data-testid="projet-confirme-check"
                     className="mt-0.5" />
              <span>Je confirme que c'est bien l'objet de ma demande.</span>
            </label>
          </div>
        </div>

        <div className="p-4 sm:p-6 space-y-6">

          {/* Q1 */}
          <Question num={1} required title="Situation du bénéficiaire des travaux"
                     help="Nous ne demandons aucun diagnostic médical. Pour « Autre situation », un conseiller étudiera votre situation.">
            <RadioGroup
              name="q1_beneficiaire" options={BENEF_OPTIONS}
              value={answers.q1_beneficiaire} onChange={(v) => setA({ q1_beneficiaire: v })}
              testId="q1"
            />
          </Question>

          {/* Q2 */}
          <Question num={2} required title="Statut d'occupation pour ce logement"
                     help={state.data.prefill?.q2_occupation ? 'Préréglé d\'après votre formulaire — à confirmer.' : null}>
            <RadioGroup
              name="q2_occupation" options={OCCUPATION_OPTIONS}
              value={answers.q2_occupation} onChange={(v) => setA({ q2_occupation: v })}
              testId="q2"
            />
            {answers.q2_occupation === 'proprio_bailleur' && (
              <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-[11px] text-amber-900">
                <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
                Les propriétaires bailleurs font l'objet d'une étude spécifique différente du parcours MaPrimeAdapt' standard. Un conseiller vous recontactera.
              </div>
            )}
          </Question>

          {/* Q2a — si locataire */}
          {isLocataire && (
            <Question num="2a" required title="Accord écrit du bailleur pour les travaux">
              <RadioGroup
                name="q2a_accord_bailleur" options={BAILLEUR_OPTIONS}
                value={answers.q2a_accord_bailleur} onChange={(v) => setA({ q2a_accord_bailleur: v })}
                testId="q2a"
              />
            </Question>
          )}

          {/* Q3 */}
          <Question num={3} required title="Ce logement est-il la résidence principale du bénéficiaire ?">
            <RadioGroup
              name="q3_residence_principale" options={RESIDENCE_OPTIONS}
              value={answers.q3_residence_principale} onChange={(v) => setA({ q3_residence_principale: v })}
              testId="q3"
            />
          </Question>

          {/* Q4 */}
          <Question num={4} title="Code postal du logement">
            <input
              type="text" inputMode="numeric" maxLength={5}
              className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-base"
              placeholder="77100"
              value={answers.q4_code_postal || ''}
              disabled={answers.q4_cp_inconnu}
              onChange={(e) => setA({ q4_code_postal: e.target.value.replace(/\D/g, '') })}
              data-testid="q4-cp-input"
            />
            <label className="flex items-center gap-2 mt-2 text-xs text-slate-600 cursor-pointer">
              <input type="checkbox" checked={!!answers.q4_cp_inconnu}
                     onChange={(e) => setA({ q4_cp_inconnu: e.target.checked,
                                              q4_code_postal: e.target.checked ? '' : answers.q4_code_postal })}
                     data-testid="q4-cp-unknown" />
              Je ne connais pas le code postal
            </label>
          </Question>

          {/* Q5 */}
          <Question num={5} title="Nombre de personnes composant le foyer fiscal du bénéficiaire">
            <input
              type="number" inputMode="numeric" min={1}
              className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-base"
              placeholder="Ex : 2"
              value={answers.q5_nb_personnes || ''}
              disabled={answers.q5_nb_personnes_inconnu}
              onChange={(e) => setA({ q5_nb_personnes: parseInt(e.target.value, 10) || null })}
              data-testid="q5-nbpers-input"
            />
            <label className="flex items-center gap-2 mt-2 text-xs text-slate-600 cursor-pointer">
              <input type="checkbox" checked={!!answers.q5_nb_personnes_inconnu}
                     onChange={(e) => setA({ q5_nb_personnes_inconnu: e.target.checked,
                                              q5_nb_personnes: e.target.checked ? null : answers.q5_nb_personnes })}
                     data-testid="q5-nbpers-unknown" />
              Je ne sais pas
            </label>
          </Question>

          {/* Q6 */}
          <Question num={6} title="Revenu fiscal de référence annuel du foyer (en euros)"
                     help="Montant visible sur votre avis d'imposition, à la ligne « Revenu fiscal de référence ». Si plusieurs membres du foyer ont leur propre avis d'imposition, additionnez les RFR.">
            <div className="relative">
              <input
                type="number" inputMode="numeric" min={0}
                className="w-full border border-slate-200 rounded-lg pl-3 pr-8 py-2.5 text-base"
                placeholder="Ex : 24 500"
                value={answers.q6_rfr_eur || ''}
                disabled={answers.q6_rfr_inconnu}
                onChange={(e) => setA({ q6_rfr_eur: parseInt(e.target.value, 10) || null })}
                data-testid="q6-rfr-input"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">€</span>
            </div>
            <label className="flex items-center gap-2 mt-2 text-xs text-slate-600 cursor-pointer">
              <input type="checkbox" checked={!!answers.q6_rfr_inconnu}
                     onChange={(e) => setA({ q6_rfr_inconnu: e.target.checked,
                                              q6_rfr_eur: e.target.checked ? null : answers.q6_rfr_eur })}
                     data-testid="q6-rfr-unknown" />
              Je ne sais pas
            </label>
          </Question>

          <p className="text-[11px] text-slate-400 pt-2 border-t border-slate-100">
            Vos réponses sont enregistrées automatiquement toutes les 3 secondes.
            Vous pouvez fermer cette page et revenir plus tard. Vos données restent
            dans l'espace sécurisé Reno Resolve et ne sont jamais renvoyées à Meta / Facebook.
          </p>
        </div>

        {/* Sticky footer submit */}
        <div className="sticky bottom-0 bg-white border-t border-slate-200 p-3 sm:p-4">
          <button
            onClick={submit}
            disabled={submitting}
            className="w-full bg-[#1C49A6] hover:bg-[#173B85] text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="quiz-submit-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {submitting ? 'Envoi…' : 'Envoyer mes réponses'}
          </button>
          <p className="text-[10px] text-slate-400 text-center mt-1.5">
            La soumission est définitive. Vérifiez avant d'envoyer.
          </p>
        </div>
      </div>
    </div>
  );
}

function Question({ num, title, help, required, children }) {
  return (
    <div className="space-y-2">
      <div className="flex items-start gap-2">
        <span className="shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#1C49A6] text-white text-xs font-bold">
          {num}
        </span>
        <div>
          <div className="text-sm sm:text-base font-semibold text-slate-900 leading-snug">
            {title} {required && <span className="text-red-500">*</span>}
          </div>
          {help && (
            <div className="text-[11px] text-slate-500 mt-0.5 flex items-start gap-1">
              <HelpCircle className="w-3 h-3 mt-0.5 shrink-0" />
              <span>{help}</span>
            </div>
          )}
        </div>
      </div>
      <div>{children}</div>
    </div>
  );
}

function RadioGroup({ name, options, value, onChange, testId }) {
  return (
    <div className="space-y-1.5" data-testid={`${testId}-group`}>
      {options.map((o) => (
        <label key={o.v} className={`flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer transition
          ${value === o.v ? 'border-[#1C49A6] bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}>
          <input
            type="radio" name={name} value={o.v}
            checked={value === o.v}
            onChange={() => onChange(o.v)}
            className="mt-0.5"
            data-testid={`${testId}-${o.v}`}
          />
          <span className="text-sm text-slate-900">{o.label}</span>
        </label>
      ))}
    </div>
  );
}
